import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { registerSW } from 'virtual:pwa-register'
import './index.css'
import App from './App.jsx'
import { queryClient } from './lib/queryClient.js'
import { beginLanguageSwitchCover } from './lib/googleTranslate.js'

// ── Canonical-host guard ────────────────────────────────────────────────────
// Cloudflare Pages serves this app on BOTH the custom domains (swotify.in,
// <school>.swotify.in) and its raw production alias swotify.pages.dev. The PWA
// manifest is relative, so whatever origin a user installs/opens from becomes
// the app's locked start_url. If anyone ever lands on swotify.pages.dev (a
// shared/stale link, a search result, or an early install before the custom
// domain was wired) and installs, every launch opens swotify.pages.dev — the
// exposed URL clients are not meant to see.
//
// Bounce any pages.dev load to the canonical domain. This runs before the SW is
// registered and before React mounts, so:
//   • a visitor on pages.dev is redirected before they can install, and
//   • an already-bad install self-heals — the SW updates same-origin on next
//     launch, loads this guard, and redirects. (Deliberately NOT an edge 301:
//     that would break the SW's same-origin update and strand bad installs.)
// Best-effort: keep the tenant if a prior login on this origin recorded it,
// otherwise land on the apex school picker. Never throws — a blank redirect is
// worse than the status quo.
;(() => {
  try {
    if (typeof window === 'undefined') return
    const host = window.location.hostname.toLowerCase()
    if (host !== 'swotify.pages.dev' && host !== 'www.swotify.pages.dev') return
    let target = 'https://swotify.in'
    try {
      const code = window.localStorage.getItem('swotify_school_code')
      if (code && /^[a-z0-9][a-z0-9-]{0,62}$/i.test(code)) {
        target = `https://${code.toLowerCase()}.swotify.in`
      }
    } catch {
      /* localStorage blocked — fall back to apex */
    }
    const { pathname, search, hash } = window.location
    window.location.replace(target + pathname + search + hash)
  } catch {
    /* never block boot on the guard */
  }
})()

const localPushDev =
  import.meta.env.VITE_SW_DEV === 'true' &&
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')

if (import.meta.env.DEV && !localPushDev) {
  // In development, never let a previously-installed service worker (from a
  // prior production build or an SW_DEV run) serve a stale cached bundle — it
  // silently masks code changes. Unregister any SW and drop its caches so the
  // Vite dev server's fresh modules always win.
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((reg) => reg.unregister())
    })
  }
  if (typeof caches !== 'undefined') {
    caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)))
  }
} else if (import.meta.env.DEV && localPushDev && 'serviceWorker' in navigator) {
  // virtual:pwa-register is a no-op while Vite is in dev, and injectRegister
  // is null. Register /sw.js (no query string) so Chrome's push service accepts
  // this device. A closed window can then show the system notification.
  navigator.serviceWorker.getRegistrations().then(async (regs) => {
    await Promise.all(
      regs
        .filter((reg) => {
          const url = reg.active?.scriptURL || reg.installing?.scriptURL || reg.waiting?.scriptURL || ''
          return url.includes('dev-sw.js?')
        })
        .map((reg) => reg.unregister()),
    )
    return navigator.serviceWorker.register('/sw.js', { scope: '/', type: 'module' })
  })
    .then((registration) => {
      window.__swotifySW = registration
      window.dispatchEvent(new Event('swotify-sw-ready'))
    })
    .catch((err) => {
      console.error('[SWOTIFY PLUS] Dev service worker failed', err)
    })
} else {
  const updateSW = registerSW({
    immediate: true,
    onRegisteredSW(_swUrl, registration) {
      if (registration && typeof window !== 'undefined') {
        window.__swotifySW = registration
        window.dispatchEvent(new Event('swotify-sw-ready'))
      }
      // Long-lived tabs: re-check for a new deploy every hour so updates land
      // without the user having to fully restart the app.
      if (registration) {
        setInterval(() => {
          registration.update().catch(() => {})
        }, 60 * 60 * 1000)
      }
    },
    onOfflineReady() {
      console.info('[SWOTIFY PLUS] App ready to work offline (cached shell).')
    },
    onNeedRefresh() {
      // autoUpdate: a new build is available — apply it and reload to the fresh
      // version. Only fires on a genuine update, so no reload loop.
      console.info('[SWOTIFY PLUS] New version available — updating…')
      updateSW(true)
    },
  })
}

beginLanguageSwitchCover()

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
)

/** Opening splash: play the logo-reveal video fully, then fade (uses #pwa-splash in index.html).
 *  Mobile (<768px) uses the portrait reveal; desktop uses swotify-logo-reveal-desktop.mp4.
 *  Always plays the complete original video (no early trim); dismisses on ended. */
;(function dismissSplash() {
  const splash = document.getElementById('pwa-splash')
  if (document.documentElement.classList.contains('sw-lang-switching')) {
    splash?.remove()
    return
  }
  if (!splash) return

  const DESKTOP_MIN_WIDTH = 768
  const isDesktop = () => window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`).matches

  let done = false
  const hide = () => {
    if (done) return
    done = true
    splash.classList.add('pwa-splash--hide')
    window.setTimeout(() => splash.remove(), 480)
  }

  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  const video = document.getElementById('pwa-splash-video')
  const canPlayVideo =
    !!video && !!video.src && !reduceMotion && !!video.canPlayType?.('video/mp4')

  // Hard safety cap so the splash can never get stuck.
  const capMs = isDesktop() ? 20000 : 15000
  const capTimer = window.setTimeout(hide, capMs)
  const finish = () => {
    window.clearTimeout(capTimer)
    hide()
  }

  if (!canPlayVideo) {
    // Static fallback: brief minimum display, then fade.
    const t0 = performance.now()
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        window.setTimeout(finish, Math.max(0, 850 - (performance.now() - t0))),
      ),
    )
    return
  }

  video.addEventListener('error', () => window.setTimeout(finish, 600), { once: true })
  video.addEventListener('ended', finish, { once: true })

  const tryPlay = () => {
    const p = video.play()
    if (p && typeof p.catch === 'function') {
      // Autoplay blocked — don't hang on the reveal, fade after a brief hold.
      p.catch(() => window.setTimeout(finish, 900))
    }
  }
  if (video.readyState >= 2) tryPlay()
  else video.addEventListener('loadeddata', tryPlay, { once: true })
})()
